import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { PayoutRequest } from '../../types';
import { formatDateTimeIst } from '../../utils/dateUtils';

const PayoutApprovalPage: React.FC = () => {
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedPayout, setSelectedPayout] = useState<PayoutRequest | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isApproving, setIsApproving] = useState(false);

  const fetchPayouts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await apiService.getPayoutRequests({
        status: statusFilter || undefined,
        per_page: 50,
      });
      setPayouts(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payout requests');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const handleApprove = async (payout: PayoutRequest) => {
    setSelectedPayout(payout);
    setApprovalDialogOpen(true);
    setIsApproving(true);
  };

  const handleReject = async (payout: PayoutRequest) => {
    setSelectedPayout(payout);
    setApprovalDialogOpen(true);
    setIsApproving(false);
  };

  const handleSubmitApproval = async () => {
    if (!selectedPayout) return;

    try {
      await apiService.approvePayoutRequest(selectedPayout.id, {
        approved: isApproving,
        rejection_reason: !isApproving ? rejectionReason : undefined,
      });

      setApprovalDialogOpen(false);
      setRejectionReason('');
      setSelectedPayout(null);
      fetchPayouts();
      alert(isApproving ? 'Payout approved successfully' : 'Payout rejected successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to process payout request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'processed':
        return 'info';
      default:
        return 'default';
    }
  };

  const pendingPayouts = payouts.filter(p => p.status === 'pending');
  const totalPendingAmount = pendingPayouts.reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Payout Approval
      </Typography>

      {/* Summary Cards */}
      <Box display="flex" gap={2} mb={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" variant="body2">
              Pending Requests
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F57C00' }}>
              {pendingPayouts.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" variant="body2">
              Total Pending Amount
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F57C00' }}>
              ₹{totalPendingAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" variant="body2">
              Total Requests
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {payouts.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchPayouts}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Professional</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Bank Details</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.map((payout) => (
                <TableRow key={payout.id}>
                  <TableCell>{payout.payout_reference}</TableCell>
                  <TableCell>{payout.user?.name || 'N/A'}</TableCell>
                  <TableCell>₹{parseFloat(payout.amount.toString()).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payout.bank_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {payout.account_holder_name} • {payout.bank_account_number.slice(-4)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payout.status}
                      color={getStatusColor(payout.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(payout.created_at).toLocaleDateString('en-IN')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedPayout(payout);
                        setApprovalDialogOpen(true);
                        setIsApproving(true);
                      }}
                      color="primary"
                      disabled={payout.status !== 'pending'}
                    >
                      <ViewIcon />
                    </IconButton>
                    {payout.status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleApprove(payout)}
                          color="success"
                        >
                          <ApproveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleReject(payout)}
                          color="error"
                        >
                          <RejectIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approval/Rejection Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isApproving ? 'Approve Payout Request' : 'Reject Payout Request'}
        </DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <Box>
              <Typography><strong>Reference:</strong> {selectedPayout.payout_reference}</Typography>
              <Typography><strong>Professional:</strong> {selectedPayout.user?.name || 'N/A'}</Typography>
              <Typography><strong>Amount:</strong> ₹{parseFloat(selectedPayout.amount.toString()).toFixed(2)}</Typography>
              <Typography><strong>Bank Name:</strong> {selectedPayout.bank_name}</Typography>
              <Typography><strong>Account Holder:</strong> {selectedPayout.account_holder_name}</Typography>
              <Typography><strong>Account Number:</strong> {selectedPayout.bank_account_number}</Typography>
              <Typography><strong>IFSC:</strong> {selectedPayout.bank_ifsc}</Typography>
              <Typography><strong>Requested:</strong> {formatDateTimeIst(selectedPayout.created_at)}</Typography>

              {!isApproving && (
                <TextField
                  fullWidth
                  label="Rejection Reason"
                  multiline
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setApprovalDialogOpen(false);
            setRejectionReason('');
            setSelectedPayout(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitApproval}
            color={isApproving ? 'success' : 'error'}
            variant="contained"
            disabled={!isApproving && !rejectionReason}
          >
            {isApproving ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutApprovalPage;

